/**
 * NmvtisMarginPanel — one month of NMVTIS revenue against AAMVA's charge, with a what-if on the default prices.
 *
 * AAMVA bills graduated on our total volume across all clients, and only for VINs where NMVTIS found a record,
 * while clients are billed graduated on their own volume. The two tables are counted differently, so the only
 * honest way to judge a price change is to re-run this month's real volumes through it, which is what the
 * what-if does: clients on the default tiers are re-priced with the proposed bands; clients on their own tiers
 * or a negotiated price keep what they billed.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Activity, AlertTriangle, Check, Loader2, Save } from 'lucide-react';
import { getErrorMessage } from '../../../services/apiService';
import { BandRow, graduatedTotal, saveDefaultPricingTiers, toBandItems, validateBandRows } from '../../../services/pricingTiersService';
import { NmvtisMarginReport, PricingSource, bandsToRows, money } from '../../../services/marginsService';
import PricingTierBandEditor from '../PricingTierBandEditor';

const SOURCE_LABEL: Record<PricingSource, string> = {
  DefaultTiers: 'Default tiers',
  OwnTiers: 'Own tiers',
  Negotiated: 'Negotiated price',
  Flat: 'Flat price',
};

const Stat: React.FC<{ label: string; value: string; sub?: string; tone?: 'good' | 'bad' }> = ({ label, value, sub, tone }) => (
  <div className="rounded-xl border border-black/[0.06] bg-white/70 px-4 py-3">
    <div className="text-xs uppercase tracking-wider text-[#94a3b8]">{label}</div>
    <div className={`text-xl font-bold mt-1 ${tone === 'good' ? 'text-emerald-700' : tone === 'bad' ? 'text-red-700' : 'text-[#0f172a]'}`}>{value}</div>
    {sub && <div className="text-xs text-[#64748b] mt-0.5">{sub}</div>}
  </div>
);

interface Props {
  report: NmvtisMarginReport;
  onPricesApplied: () => void;
}

const NmvtisMarginPanel: React.FC<Props> = ({ report, onPricesApplied }) => {
  const currentRows = useMemo(() => bandsToRows(report.defaultBands), [report.defaultBands]);
  const [rows, setRows] = useState<BandRow[]>(currentRows);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { setRows(currentRows); setConfirming(false); }, [currentRows]);

  const invalid = useMemo(() => validateBandRows(rows), [rows]);
  const changed = JSON.stringify(rows) !== JSON.stringify(currentRows);
  const defaultTierClients = report.clients.filter((c) => c.pricingSource === 'DefaultTiers');

  // Re-price only the clients the default table reaches; keep what they billed beyond the band math
  // (a mid-month price change, cent rounding) by swapping the current bands' total for the proposed one.
  const whatIf = useMemo(() => {
    if (invalid) return null;
    const delta = (volume: (c: typeof defaultTierClients[number]) => number) =>
      defaultTierClients.reduce((sum, c) => sum + graduatedTotal(rows, volume(c)) - graduatedTotal(currentRows, volume(c)), 0);
    const revenue = report.revenue + delta((c) => c.reports);
    const projectedRevenue = report.projection ? report.projection.revenue + delta((c) => c.projectedReports) : null;
    return {
      revenue,
      margin: revenue - report.actual.aamvaCost,
      projectedRevenue,
      projectedMargin: report.projection && projectedRevenue != null ? projectedRevenue - report.projection.cost.aamvaCost : null,
    };
  }, [invalid, rows, currentRows, defaultTierClients, report]);

  const apply = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await saveDefaultPricingTiers('NMVTIS', toBandItems(rows));
      setSuccess('New default NMVTIS prices saved. They apply from each client\'s next lookup.');
      setConfirming(false);
      onPricesApplied();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const a = report.actual;
  const p = report.projection;
  const foundPct = report.aamvaCalls > 0 ? Math.round((report.recordsFound / report.aamvaCalls) * 1000) / 10 : null;

  return (
    <div className="glass-card overflow-hidden">
      <div className="px-6 py-4 border-b border-black/[0.06] flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[#475569] flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#c41e3a]" /> NMVTIS
        </h2>
        <span className="text-xs text-[#64748b]">
          AAMVA schedule effective {report.scheduleEffectiveFrom ? report.scheduleEffectiveFrom.slice(0, 10) : '— none on file'}
        </span>
      </div>

      <div className="px-6 py-5 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat label="Reports billed" value={report.reports.toLocaleString()} />
          <Stat label="Revenue" value={money(report.revenue)} sub={report.reports ? `${money(report.revenue / report.reports, 3)} avg` : undefined} />
          <Stat label="Records found" value={report.recordsFound.toLocaleString()} sub={`of ${report.aamvaCalls.toLocaleString()} AAMVA calls${foundPct != null ? ` (${foundPct}%)` : ''}`} />
          <Stat label="AAMVA cost (est.)" value={money(a.aamvaCost)} sub={a.minimumApplies ? `$${report.monthlyMinimum.toLocaleString()} minimum applies` : `hits ${money(a.hitCharges)} + ${money(report.monthlyServiceFee)} fee`} />
          <Stat label="Margin" value={money(a.margin)} tone={a.margin >= 0 ? 'good' : 'bad'} />
          <Stat label="Margin / report" value={money(a.marginPerReport, 3)} tone={(a.marginPerReport ?? 0) >= 0 ? 'good' : 'bad'} />
        </div>

        {p && (
          <div className="rounded-xl border border-dashed border-black/[0.12] px-4 py-3 text-sm text-[#334155]">
            <span className="font-semibold">Month-end at this pace</span> ({Math.round(report.monthFractionElapsed * 100)}% of the month gone):{' '}
            {p.reports.toLocaleString()} reports, revenue {money(p.revenue)}, AAMVA {money(p.cost.aamvaCost)},{' '}
            <span className={p.cost.margin >= 0 ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>margin {money(p.cost.margin)}</span>
          </div>
        )}

        <p className="text-xs text-[#64748b]">
          Revenue is what we billed. AAMVA's cost is estimated: it charges only for VINs where NMVTIS found a record, counted
          here from our call log, graduated on our total volume. Their invoice is the final word.
        </p>

        <div>
          <h3 className="text-sm font-semibold text-[#0f172a] mb-2">By client</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-[#94a3b8] border-b border-black/[0.06]">
                <tr>
                  <th className="text-left py-2">Client</th>
                  <th className="text-left">Pricing</th>
                  <th className="text-right">Reports</th>
                  <th className="text-right">Revenue</th>
                  <th className="text-right">Avg price</th>
                  {p && <th className="text-right">Projected</th>}
                </tr>
              </thead>
              <tbody>
                {report.clients.length === 0 && (
                  <tr><td colSpan={6} className="py-4 text-center text-[#64748b]">No billable NMVTIS reports this month.</td></tr>
                )}
                {report.clients.map((c) => (
                  <tr key={c.clientId ?? 'none'} className="border-b border-black/[0.04]">
                    <td className="py-2">{c.clientName}</td>
                    <td className="text-[#64748b]">{SOURCE_LABEL[c.pricingSource]}</td>
                    <td className="text-right">{c.reports.toLocaleString()}</td>
                    <td className="text-right">{money(c.revenue)}</td>
                    <td className="text-right">{money(c.averagePrice, 3)}</td>
                    {p && <td className="text-right text-[#64748b]">{c.projectedReports.toLocaleString()} / {money(c.projectedRevenue)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="border-t border-black/[0.06] pt-5">
          <h3 className="text-sm font-semibold text-[#0f172a] mb-1">Try different default prices</h3>
          <p className="text-xs text-[#64748b] mb-3">
            Edit the bands to see what this month would have earned with them. {defaultTierClients.length} client
            {defaultTierClients.length === 1 ? '' : 's'} on the default tiers {defaultTierClients.length === 1 ? 'is' : 'are'} re-priced;
            clients on their own tiers or a negotiated price keep what they billed. Nothing changes until you apply.
          </p>

          <PricingTierBandEditor rows={rows} onChange={(r) => { setRows(r); setConfirming(false); }} disabled={saving} />

          {whatIf && changed && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-xl border border-black/[0.06] bg-white/70 px-4 py-3 text-sm">
                <div className="text-xs uppercase tracking-wider text-[#94a3b8]">This month so far, with these prices</div>
                <div className="mt-1">Revenue {money(whatIf.revenue)} <span className="text-[#64748b]">({money(whatIf.revenue - report.revenue)} vs actual)</span></div>
                <div className={whatIf.margin >= 0 ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>Margin {money(whatIf.margin)}</div>
              </div>
              {whatIf.projectedRevenue != null && whatIf.projectedMargin != null && (
                <div className="rounded-xl border border-black/[0.06] bg-white/70 px-4 py-3 text-sm">
                  <div className="text-xs uppercase tracking-wider text-[#94a3b8]">Month-end at this pace, with these prices</div>
                  <div className="mt-1">Revenue {money(whatIf.projectedRevenue)}</div>
                  <div className={whatIf.projectedMargin >= 0 ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>Margin {money(whatIf.projectedMargin)}</div>
                </div>
              )}
            </div>
          )}

          {error && <div className="mt-4 p-3 rounded-lg border bg-red-50 border-red-200 text-sm text-red-800">{error}</div>}
          {success && <div className="mt-4 p-3 rounded-lg border bg-emerald-50 border-emerald-200 text-sm text-emerald-800">{success}</div>}

          <div className="flex justify-end items-center gap-3 mt-4">
            {confirming && (
              <span className="text-xs text-amber-800 inline-flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" /> Changes what every default-tier client pays from their next lookup.
              </span>
            )}
            {changed && (
              <button onClick={() => { setRows(currentRows); setConfirming(false); }} disabled={saving}
                className="text-sm px-3 py-2 rounded-lg border border-black/[0.12] text-[#475569] hover:bg-black/[0.03]">
                Reset
              </button>
            )}
            <button
              onClick={() => (confirming ? apply() : setConfirming(true))}
              disabled={!changed || !!invalid || saving}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-[#c41e3a] text-white hover:bg-[#a51830] disabled:opacity-40"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : confirming ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              {confirming ? 'Confirm new prices' : 'Apply as default prices'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NmvtisMarginPanel;
